import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableService from "../../../services/OrderingServices/TableService";
import toast from "react-hot-toast";
import SectionService from "../../../services/OrderingServices/SectionService";

const statusMap = {
  Available: 1,
  Reserved: 2,
  Occupied: 3,
  Unavailable: 4,
  UnderMaintenance: 5,
};

const CreateTable = ({ isOpen, toggle, selectedTable, refreshData }) => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState();
  const [loading, setLoading] = useState(false);

  const fetchTableData = async () => {
    try {
      const response = await TableService.detail(selectedTable?.id);
      setTableData(response);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    fetchTableData();
  }, [selectedTable?.id]);
  const [sectionData, setSectionData] = useState([]);
  const fetchSection = async () => {
    try {
      const response = await SectionService.getList();
      // console.log("🚀 ~ fetchSection ~ response:", response);
      setSectionData(response);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    fetchSection();
  }, []);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: tableData?.id || "",
      tableName: tableData?.tableName || "",
      description: tableData?.description || "",
      capacity: tableData?.capacity || "",
      isAvailable: tableData?.isAvailable ?? true,
      sectionId: tableData?.section?.id || "",
      isRoom: tableData?.isRoom || false,
      tableStatus: tableData?.tableStatus
        ? statusMap[tableData.tableStatus] ?? 1
        : 1,
    },
    validationSchema: Yup.object({
      tableName: Yup.string()
        .required("Table name is required")
        .max(50, "Table name must be under 50 characters"),

      description: Yup.string()
        .max(255, "Description must be under 255 characters")
        .nullable(),

      capacity: Yup.number()
        .min(1, "Capacity must be at least 1")
        .max(100, "Capacity must be 100 or less")
        .required("Capacity is required"),

      sectionId: Yup.number()
        .typeError("Section is required") // handles string value cases
        .required("Section is required"),

      tableStatus: Yup.number().required("Table status is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);

        if (values.id) {
          await TableService.update(values.id, values);
          toast.success("Table updated successfully.");
        } else {
          await TableService.create(values);
          toast.success("Table created successfully.");
        }
        refreshData();
        toggle();
        resetForm();
      } catch (error) {
        toast.error("Failed to save table.");
      } finally {
        setLoading(false);
      }
    },
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      formik.resetForm();
    }
  }, [isOpen]);

  const sectionOptions = sectionData?.map((item) => (
    <option key={item.id} value={item.id}>
      {item.name}
    </option>
  ));

  return (
    <Offcanvas 
      isOpen={isOpen} 
      toggle={() => toggle(null)}
      direction="end"
      backdropClassName="bg-dark bg-opacity-50"
      className="shadow-lg animate__animated animate__slideInRight bg-white"
      >
      <OffcanvasHeader toggle={() => toggle(null)}>
        {selectedTable?.id ? "Update Table" : "Create Table"}
      </OffcanvasHeader>
      
      <form onSubmit={formik.handleSubmit}>
        <OffcanvasBody>
          <Row >
            
              {/* Table Name */}
              <div className="mb-3">
                <label className="form-label">Table Name</label>
                <input
                  type="text"
                  name="tableName"
                  className={`form-control ${
                    formik.touched.tableName && formik.errors.tableName
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.tableName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.tableName && formik.errors.tableName && (
                  <div className="invalid-feedback">
                    {formik.errors.tableName}
                  </div>
                )}
              </div>
           
            
              {/* Capacity */}
              <div className="mb-3">
                <label className="form-label">Capacity</label>
                <input
                  type="number"
                  name="capacity"
                  className={`form-control ${
                    formik.touched.capacity && formik.errors.capacity
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.capacity}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  min={1}
                />
                {formik.touched.capacity && formik.errors.capacity && (
                  <div className="invalid-feedback">
                    {formik.errors.capacity}
                  </div>
                )}
              </div>
            

            
              <div className="mb-3">
                <label className="form-label">Section</label>
                <select
                  name="sectionId"
                  className={`form-select ${
                    formik.touched.sectionId && formik.errors.sectionId
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.sectionId}
                  onChange={(e) =>
                    formik.setFieldValue("sectionId", Number(e.target.value))
                  }
                >
                  <option value="">-- Select Section --</option>
                  {sectionOptions}
                </select>
                {formik.touched.sectionId && formik.errors.sectionId && (
                  <div className="invalid-feedback">
                    {formik.errors.sectionId}
                  </div>
                )}
              </div>
             
             
          
             
             
            
            
              {/* Table Status */}
              <div className="mb-3">
                <label className="form-label">Table Status</label>
                <select
                  name="tableStatus"
                  className={`form-select ${
                    formik.touched.tableStatus && formik.errors.tableStatus
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.tableStatus}
                  onChange={(e) =>
                    formik.setFieldValue("tableStatus", Number(e.target.value))
                  }
                  onBlur={formik.handleBlur} // Important to trigger 'touched'
                >
                  <option value="">Select status</option>
                  <option value={1}>Available</option>
                  <option value={2}>Reserved</option>
                  <option value={3}>Occupied</option>
                  <option value={4}>Unavailable</option>
                  <option value={5}>Under Maintenance</option>
                </select>
                {formik.touched.tableStatus && formik.errors.tableStatus && (
                  <div className="invalid-feedback">
                    {formik.errors.tableStatus}
                  </div>
                )}
              </div>
            
            
              {/* Description */}
              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  className={`form-control ${
                    formik.touched.description && formik.errors.description
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.description && formik.errors.description && (
                  <div className="invalid-feedback">
                    {formik.errors.description}
                  </div>
                )}
              </div>
           
            
              {/* Availability */}
            <Col md= {12}>
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  name="isRoom"
                  className="form-check-input"
                  checked={formik.values.isRoom}
                  onChange={formik.handleChange}
                />
                <label className="form-check-label">Is Room</label>
              </div>
            </Col>
            <Col md={12}>
              {/* Availability */}
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  name="isAvailable"
                  className="form-check-input"
                  checked={formik.values.isAvailable}
                  onChange={formik.handleChange}
                />
                <label className="form-check-label">Is Available</label>
              </div>
            </Col>
          </Row>
        </OffcanvasBody>
        <div className = "d-flex bg-white align-content-center align-items-center justify-content-between gap-2 position-absolute bottom-0 start-0 end-0 p-3">

        
          {/* <button type="submit" className="btn btn-success">
            {selectedTable?.id ? "Update Table" : "Create Table"}
          </button> */}

          <Button color="danger" onClick={toggle} className="w-100">
            Cancel
          </Button>
          <Button color="success" type="submit" disabled={loading} className="w-100">
            {loading && <Spinner size="sm" className="me-2" />}
            {loading
              ? selectedTable?.id
                ? "Updating..."
                : "Creating..."
              : selectedTable?.id
              ? "Update"
              : "Create"}
          </Button>
        </div>
      </form>
    </Offcanvas>
  );
};

export default CreateTable;
